import React from 'react';
import { MapType, Methodology, PairRow, PlotSummary, RefRow } from '../services/apiService';

/* ------------------------------------------------------------------ */
/* Formatting helpers                                                  */
/* ------------------------------------------------------------------ */

/** 0 -> "0%", 0 < v < 0.01 -> "<0.01%", otherwise 2 decimals. */
export const formatPercent = (v: number | null | undefined): string => {
  if (v === null || v === undefined || Number.isNaN(v)) return 'N/A';
  if (v === 0) return '0%';
  if (v > 0 && v < 0.01) return '<0.01%';
  return `${v.toFixed(2)}%`;
};

export const formatHa = (v: number | null | undefined, digits = 4): string =>
  v === null || v === undefined || Number.isNaN(v) ? 'N/A' : v.toFixed(digits);

export const formatM2 = (v: number | null | undefined): string => {
  if (v === null || v === undefined || Number.isNaN(v)) return 'N/A';
  return v < 10 ? v.toFixed(2) : Math.round(v).toLocaleString();
};

const RISK_CLASS: { [level: string]: string } = {
  Low: 'risk-low',
  Medium: 'risk-medium',
  High: 'risk-high',
  Minor: 'risk-low',
  Moderate: 'risk-medium',
  Significant: 'risk-high',
};

export const riskClass = (level?: string): string => (level && RISK_CLASS[level]) || 'risk-none';

const COMPLIANCE_CLASS: { [status: string]: string } = {
  Compliant: 'compliance-pass',
  Monitor: 'compliance-warning',
  'Requires Review': 'compliance-fail',
};

export const complianceClass = (status?: string): string =>
  (status && COMPLIANCE_CLASS[status]) || '';

const RISK_RANK: { [level: string]: number } = {
  Low: 1,
  Minor: 1,
  Medium: 2,
  Moderate: 2,
  High: 3,
  Significant: 3,
};

export const highestRisk = (levels: (string | undefined)[]): string | undefined =>
  levels.reduce<string | undefined>((best, lvl) => {
    if (!lvl || !(lvl in RISK_RANK)) return best;
    if (!best || RISK_RANK[lvl] > RISK_RANK[best]) return lvl;
    return best;
  }, undefined);

/* ------------------------------------------------------------------ */
/* Row accessors (new contract first, legacy keys as fallback)         */
/* ------------------------------------------------------------------ */

export type RefCategory = 'forest_reserve' | 'protected_area' | 'forest_cover';

/** Plot code of a pair side, or undefined for old results (which only carried the producer). */
export const pairCode = (row: PairRow, side: 1 | 2): string | undefined =>
  side === 1 ? row.plot1_name || undefined : row.plot2_name || undefined;

export const pairLabel = (row: PairRow, side: 1 | 2): string =>
  pairCode(row, side) ||
  (side === 1 ? row.plot1_producer : row.plot2_producer) ||
  'Unknown';

/** Severity from backend; legacy rows without it fall back to the old thresholds. */
export const pairSeverity = (row: PairRow): string => {
  if (row.severity) return row.severity;
  const pct = Math.max(row.overlap_percentage1 || 0, row.overlap_percentage2 || 0);
  return pct < 1 ? 'Minor' : pct < 5 ? 'Moderate' : 'Significant';
};

export const refPlotCode = (row: RefRow): string | undefined => row.plot_name || row.name || undefined;

const REF_FALLBACK_NAME: { [c in RefCategory]: string } = {
  forest_reserve: 'Thai Forest Reserve',
  protected_area: 'Thai Protected Area',
  forest_cover: 'Forest cover 2020',
};

export const refName = (row: RefRow, category: RefCategory): string =>
  row.reference_name ||
  row.FR_NAME ||
  row.reserve_name ||
  row.protected_area_name ||
  row.name_en ||
  row.name_th ||
  REF_FALLBACK_NAME[category];

export const refCode = (row: RefRow): string | undefined => {
  const code = row.reference_code ?? row.NRF_CODE ?? row.pro_id;
  return code === null || code === undefined || code === '' ? undefined : String(code);
};

export const refProvince = (row: RefRow): string | undefined =>
  row.reference_province || row.Province || undefined;

export const refOverlapHa = (row: RefRow): number | undefined =>
  row.overlap_hectares ??
  row.forest_overlap_hectares ??
  row.protected_overlap_hectares ??
  row.forest_cover_overlap_hectares;

const LEGACY_THRESHOLDS: { [c in RefCategory]: [number, number] } = {
  forest_reserve: [5, 15],
  protected_area: [1, 5],
  forest_cover: [10, 15],
};

/** Risk level from backend; only legacy rows without it are classified locally. */
export const refRisk = (row: RefRow, category: RefCategory): string => {
  const level = row.risk_level || row.eudr_risk_level;
  if (level) return level;
  const pct = row.overlap_percentage || 0;
  const [med, high] = LEGACY_THRESHOLDS[category];
  return pct < med ? 'Low' : pct < high ? 'Medium' : 'High';
};

export const matchesFilter = (code: string | undefined, filter: string): boolean =>
  !filter || (!!code && code.toLowerCase().includes(filter));

/* ------------------------------------------------------------------ */
/* Components                                                          */
/* ------------------------------------------------------------------ */

export type ShowOnMap = (mapType: MapType, plotCode: string) => void;

interface MapButtonProps {
  plotCode?: string;
  mapType: MapType;
  mapLabel: string;
  onShowMap: ShowOnMap;
}

export const MapButton: React.FC<MapButtonProps> = ({ plotCode, mapType, mapLabel, onShowMap }) => {
  if (!plotCode) return <span className="muted">—</span>;
  return (
    <button
      type="button"
      className="map-link-btn"
      onClick={() => onShowMap(mapType, plotCode)}
      aria-label={`Show plot ${plotCode} on the ${mapLabel} map`}
      title={`Show plot ${plotCode} on the ${mapLabel} map`}
    >
      Map
    </button>
  );
};

interface AreaCheckTableProps {
  rows: PlotSummary[];
  tolerancePct: number;
  onShowMap: ShowOnMap;
}

const signed = (v: number | null | undefined, digits: number, suffix = ''): string =>
  v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(digits)}${suffix}`;

export const AreaCheckTable: React.FC<AreaCheckTableProps> = ({ rows, tolerancePct, onShowMap }) => (
  <div className="table-container">
    <table className="data-table">
      <caption className="visually-hidden">Declared vs computed plot area</caption>
      <thead>
        <tr>
          <th scope="col">#</th>
          <th scope="col">Plot Code</th>
          <th scope="col">Declared (ha)</th>
          <th scope="col">Computed (ha)</th>
          <th scope="col">Difference (ha)</th>
          <th scope="col">Difference %</th>
          <th scope="col">Within ±{tolerancePct}%</th>
          <th scope="col">Map</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={row.plot_index}>
            <td>{index + 1}</td>
            <td className="plot-code">{row.plot_name}</td>
            <td>{formatHa(row.declared_area_hectares, 2)}</td>
            <td>{formatHa(row.computed_area_hectares, 3)}</td>
            <td>{signed(row.area_difference_hectares, 3)}</td>
            <td>{signed(row.area_difference_pct, 2, '%')}</td>
            <td>
              {row.area_mismatch ? <span className="risk-high">No</span> : <span className="risk-low">Yes</span>}
            </td>
            <td>
              <MapButton plotCode={row.plot_name} mapType="all-layers" mapLabel="all layers" onShowMap={onShowMap} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

interface PairTableProps {
  rows: PairRow[];
  onShowMap: ShowOnMap;
  caption?: string;
}

export const PairTable: React.FC<PairTableProps> = ({ rows, onShowMap, caption }) => (
  <div className="table-container">
    <table className="data-table">
      {caption && <caption className="visually-hidden">{caption}</caption>}
      <thead>
        <tr>
          <th scope="col">#</th>
          <th scope="col">Plot A ↔ Plot B</th>
          <th scope="col">Plot A Area (ha)</th>
          <th scope="col">Plot B Area (ha)</th>
          <th scope="col">Overlap (ha)</th>
          <th scope="col">Overlap (m²)</th>
          <th scope="col">% of Plot A</th>
          <th scope="col">% of Plot B</th>
          <th scope="col">Severity</th>
          <th scope="col">Map</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => {
          const severity = pairSeverity(row);
          const m2 = row.overlap_area_m2 ?? (row.overlap_area !== undefined ? row.overlap_area * 10000 : undefined);
          return (
            <tr key={`${row.plot1_index ?? index}-${row.plot2_index ?? index}-${index}`}>
              <td>{index + 1}</td>
              <td className="plot-code">
                {pairLabel(row, 1)} <span aria-label="overlaps with">↔</span> {pairLabel(row, 2)}
                {row.same_plot_code && (
                  <div className="muted small-note">Same plot code on two different polygons — check the source data</div>
                )}
              </td>
              <td>{formatHa(row.plot1_area)}</td>
              <td>{formatHa(row.plot2_area)}</td>
              <td className="overlap-area">{formatHa(row.overlap_area)}</td>
              <td>{formatM2(m2)}</td>
              <td>{formatPercent(row.overlap_percentage1)}</td>
              <td>{formatPercent(row.overlap_percentage2)}</td>
              <td><span className={riskClass(severity)}>{severity}</span></td>
              <td>
                <MapButton
                  plotCode={pairCode(row, 1)}
                  mapType="overlapping-plots"
                  mapLabel="plot-to-plot overlap"
                  onShowMap={onShowMap}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const REF_MAP: { [c in RefCategory]: { mapType: MapType; label: string; refHeader: string } } = {
  forest_reserve: { mapType: 'forest-reserve-overlap', label: 'forest reserve', refHeader: 'Forest Reserve' },
  protected_area: { mapType: 'protected-area-overlap', label: 'protected area', refHeader: 'Protected Area' },
  forest_cover: { mapType: 'forest-cover-overlap', label: 'forest cover', refHeader: 'Reference Layer' },
};

interface RefTableProps {
  rows: RefRow[];
  category: RefCategory;
  onShowMap: ShowOnMap;
}

export const RefTable: React.FC<RefTableProps> = ({ rows, category, onShowMap }) => {
  const cfg = REF_MAP[category];
  const isForestCover = category === 'forest_cover';
  return (
    <div className="table-container">
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">Plot Code</th>
            <th scope="col">{cfg.refHeader}</th>
            <th scope="col">Overlap (ha)</th>
            <th scope="col">Overlap %</th>
            <th scope="col">Plot Area (ha, computed)</th>
            <th scope="col">Risk Level</th>
            {isForestCover && <th scope="col">Compliance Status</th>}
            <th scope="col">Map</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const code = refPlotCode(row);
            const risk = refRisk(row, category);
            const refCd = refCode(row);
            const province = refProvince(row);
            return (
              <tr key={`${row.plot_index ?? code ?? 'row'}-${refCd ?? ''}-${index}`}>
                <td className="plot-code">{code || row.producer_name || 'Unknown'}</td>
                <td>
                  <div className="reference-cell">
                    <span className="reference-name">{refName(row, category)}</span>
                    {(refCd || province) && (
                      <span className="reference-meta">
                        {refCd && <>Code {refCd}</>}
                        {refCd && province && ' · '}
                        {province}
                      </span>
                    )}
                  </div>
                </td>
                <td className="overlap-area">{formatHa(refOverlapHa(row))}</td>
                <td>{formatPercent(row.overlap_percentage)}</td>
                <td>{formatHa(row.plot_area_hectares)}</td>
                <td><span className={riskClass(risk)}>{risk}</span></td>
                {isForestCover && (
                  <td>
                    {row.compliance_status ? (
                      <span className={complianceClass(row.compliance_status)}>{row.compliance_status}</span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                )}
                <td>
                  <MapButton plotCode={code} mapType={cfg.mapType} mapLabel={cfg.label} onShowMap={onShowMap} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Methodology panel                                                   */
/* ------------------------------------------------------------------ */

const DATASET_LABELS: { [key: string]: string } = {
  forest_reserve: 'Forest reserves',
  protected_area: 'Protected areas',
  forest_cover: 'Forest cover',
};

const THRESHOLD_LABELS: { [key: string]: string } = {
  plot_overlap: 'Plot-to-plot overlap (severity)',
  forest_reserve: 'Forest reserve',
  protected_area: 'Protected area',
  forest_cover: 'Forest cover 2020',
};

/** Text with its http(s) URLs turned into links. */
const Linkified: React.FC<{ text: string }> = ({ text }) => (
  <>
    {text.split(/(https?:\/\/[^\s),]+)/).map((part, i) =>
      /^https?:\/\//.test(part) ? (
        <a key={i} href={part} target="_blank" rel="noreferrer">
          {part}
        </a>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    )}
  </>
);

/** Always-visible one-line list of the reference datasets, versions and dates used for these results. */
export const DatasetSummary: React.FC<{ methodology?: Methodology }> = ({ methodology }) => {
  const datasets = Object.entries(methodology?.datasets || {});
  if (datasets.length === 0) return null;
  return (
    <div className="dataset-summary" role="note">
      <strong>Reference data used:</strong>
      <ul>
        {datasets.map(([key, ds]) => (
          <li key={key}>
            {DATASET_LABELS[key] || key.replace(/_/g, ' ')}: {ds.name || '—'}
            {ds.version ? ` — ${ds.version}` : ''}
            {ds.published ? `, published ${ds.published}` : ''}
          </li>
        ))}
      </ul>
      <span className="muted">Sources, licences and method: see Data Sources &amp; Methodology below.</span>
    </div>
  );
};

export const MethodologyPanel: React.FC<{ methodology?: Methodology }> = ({ methodology }) => {
  if (!methodology) return null;
  const datasets = Object.entries(methodology.datasets || {});
  const thresholds = Object.entries(methodology.risk_thresholds || {});
  return (
    <details className="methodology-panel">
      <summary>
        <span className="methodology-title">Data Sources &amp; Methodology</span>
        <span className="methodology-hint">Which datasets were used and how overlaps are calculated</span>
      </summary>
      <div className="methodology-body">
        {datasets.length > 0 && (
          <>
            <h4>Reference datasets</h4>
            <div className="table-container">
              <table className="data-table methodology-table">
                <thead>
                  <tr>
                    <th scope="col">Layer</th>
                    <th scope="col">Dataset</th>
                    <th scope="col">Publisher</th>
                    <th scope="col">Version</th>
                    <th scope="col">Published</th>
                    <th scope="col">Source</th>
                    <th scope="col">Type</th>
                    <th scope="col">Coverage</th>
                  </tr>
                </thead>
                <tbody>
                  {datasets.map(([key, ds]) => (
                    <tr key={key}>
                      <th scope="row">{DATASET_LABELS[key] || key.replace(/_/g, ' ')}</th>
                      <td>{ds.name || '—'}</td>
                      <td>{ds.publisher || '—'}</td>
                      <td>{ds.version || '—'}</td>
                      <td>{ds.published || '—'}</td>
                      <td className="dataset-source">{ds.source ? <Linkified text={ds.source} /> : '—'}</td>
                      <td>{ds.type || '—'}</td>
                      <td>{ds.coverage || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <h4>Method</h4>
        <dl className="methodology-list">
          {methodology.method && (
            <>
              <dt>Calculation</dt>
              <dd>{methodology.method}</dd>
            </>
          )}
          {methodology.area_crs && (
            <>
              <dt>Area projection</dt>
              <dd>{methodology.area_crs}</dd>
            </>
          )}
          {methodology.tolerance_note && (
            <>
              <dt>Sliver tolerance</dt>
              <dd>{methodology.tolerance_note}</dd>
            </>
          )}
        </dl>

        {thresholds.length > 0 && (
          <>
            <h4>Risk thresholds (overlap % of plot area)</h4>
            <div className="table-container">
              <table className="data-table methodology-table">
                <thead>
                  <tr>
                    <th scope="col">Category</th>
                    <th scope="col">Levels</th>
                  </tr>
                </thead>
                <tbody>
                  {thresholds.map(([cat, levels]) => (
                    <tr key={cat}>
                      <th scope="row">{THRESHOLD_LABELS[cat] || cat.replace(/_/g, ' ')}</th>
                      <td>
                        <div className="threshold-levels">
                          {Object.entries(levels).map(([level, range]) => (
                            <span key={level} className="threshold-item">
                              <span className={riskClass(level)}>{level}</span> {range}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </details>
  );
};
